import React from 'react';
import Container from '@v2/shared/components/Container';
import { FullLayoutProps } from '@v2/shared/lib/types';

import Navigation from './Navigation';
import Footer from './Footer';

/**
 * How far a DiscountMags page starts off the navigation and ends off the footer.
 *
 * One declaration, in one place, is the point of it. Every page used to state a
 * variation of this for itself — `py-5 lg:py-7`, `py-8`, `pt-5 md:pt-0 pb-8`, `pb-16` —
 * so the site had six chances to drift out of agreement with itself and no way to change
 * the spacing once. The value is the one Home and the product page had each arrived at
 * separately, which makes it a standard that was already there rather than a new number.
 *
 * A page that wants more says so on top of this (see NotFound). A page that wants none
 * is not this layout's — that is `Layouts.FullWide`, which draws no box at all.
 *
 * The top half is exported because a `Layouts.FullWide` page still has to line up with
 * it. Such a page draws no box, so anything it puts at the very top — bulk orders puts
 * its breadcrumb trail there — sits at whatever padding it states for itself, and the
 * trail on a full-bleed page has to start level with the trail on every other page.
 * Stating the number again is how they drifted: bulk orders had `md:pt-5` and so sat
 * 8px high from `lg` up, where this is `pt-7`.
 */
export const CONTAINER_PADDING_TOP = 'pt-5 lg:pt-7';
const CONTAINER_PADDING_BOTTOM = 'pb-5 lg:pb-7';
const CONTAINER_PADDING = `${CONTAINER_PADDING_TOP} ${CONTAINER_PADDING_BOTTOM}`;

/**
 * The chrome every DiscountMags page shares.
 *
 * Props are FullLayoutPropsBuilder's output — the same contract headless_jshop calls
 * LayoutData — so this is the counterpart of its SiteNavigation/MainLayout pair.
 *
 * A page declares `Page.layoutName = 'Layouts.Full'` and the mount wraps it, which is
 * what collapses the navigation, the page and the footer into one React root. They
 * were three independent roots before, with no shared context between them and three
 * separate chances for a hydration mismatch once SSR lands.
 */
export type FullProps = FullLayoutProps & {
  /**
   * Whether the layout draws the page's Container. True everywhere except through
   * `Layouts.FullWide`, and never something a page passes for itself.
   *
   * It exists so that the content box is the layout's business on every brand rather
   * than on some. TopMags' Full has always opened a Container (and a padded card
   * inside it), and this one used to open a bare `<main>` and leave the box to whatever
   * went in it — so a component shared by both brands could not know whether it was
   * already boxed or spaced, and a page could not move between the two layouts without
   * checking. Now both draw the same box and neither the page nor a shared shell has to
   * ask.
   */
  container?: boolean;
};

export default function Full({
  children,
  displaySitewideBanner,
  container = true,
  copyrightYear,
  ...navigationProps
}: FullProps) {
  return (
    <>
      <Navigation {...navigationProps} />

      <main id="main" role="main">
        {/* The sitewide banner belongs here, above the page, once it is ported. */}
        {container ? <Container className={CONTAINER_PADDING}>{children}</Container> : children}
      </main>

      <Footer copyrightYear={copyrightYear} />
    </>
  );
}
